import { Text } from '@mantine/core';
import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createPaginatedRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';

import { AddIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { getAll } from '@apple/features/calendar/api';
import type { FiscalCalendarYear } from '@apple/features/calendar/models';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export const Route = createFileRoute('/_authed/_admin/manage/calendars')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.Snapshot',
		});
		return {
			calendarQueryOptions: queryOptions({
				queryKey: ['fiscal-calendar'],
				queryFn: () => getAll(),
				placeholderData: keepPreviousData,
				throwOnError: true,
				initialData: [],
			}),
		};
	},
	loader: ({ context: { queryClient, calendarQueryOptions } }) =>
		void queryClient.prefetchQuery(calendarQueryOptions),
	component: CalendarPageRoute,
});

const defaultSorting: SortingState = [{ id: 'fiscalYear', desc: false }];

function CalendarPageRoute() {
	const { t } = useTranslation('manage');
	const navigate = Route.useNavigate();
	const search = Route.useSearch();
	const { auth } = Route.useRouteContext();
	const { calendarQueryOptions } = Route.useRouteContext();
	const calendarQuery = useQuery(calendarQueryOptions);

	const tableState = useTableState<FiscalCalendarYear, unknown>({
		search,
		navigate,
		defaultFilters: {},
		defaultSorting: defaultSorting,
		fieldMap: [],
	});

	const table = useTable({
		features,
		data: calendarQuery.data,
		...tableState,
		enableFilters: false,
		enableColumnFilters: false,
		manualFiltering: false,
		enableSorting: false,
		columnResizeMode: 'onChange',
		columns: [
			{
				accessorKey: 'fiscalYear',
				header: t('calendars.headers.fiscalYear'),
				size: 180,
				cell: ({ row }) =>
					auth.hasPermission('AppleRetail.Security.Features.CalendarManagement') ? (
						<Link
							to='/manage/calendars/$fiscalYear'
							params={{ fiscalYear: row.original.fiscalYear }}
							children={row.original.fiscalYear}
						/>
					) : (
						<Text>{row.original.fiscalYear}</Text>
					),
			},
			{
				accessorKey: 'startDate',
				header: t('calendars.headers.startDate'),
				size: 300,
				cell: ({ row }) => <FormattedDate value={row.original.startDate} />,
			},
			{
				accessorKey: 'endDate',
				header: t('calendars.headers.endDate'),
				size: 300,
				cell: ({ row }) => <FormattedDate value={row.original.endDate} />,
			},
		],
	});

	return (
		<TableLayout
			table={table}
			title={t('calendars.title')}
			toolbarButtons={[
				auth.hasPermission('AppleRetail.Security.Features.CalendarManagement') && (
					<ToolbarButton
						key='add'
						tooltip={t('calendars.add.title')}
						icon={AddIcon}
						onClick={() =>
							void navigate({
								to: '/manage/calendars/add',
								viewTransition: false,
							})
						}
					/>
				),
			]}
		>
			<DataTable table={table} loading={calendarQuery.isFetching} />
		</TableLayout>
	);
}
